import { Material } from '@babylonjs/core/Materials/material';
import { PBRMaterial } from '@babylonjs/core/Materials/PBR/pbrMaterial';
import { BaseTexture, DynamicTexture, Texture } from '@babylonjs/core/Materials/Textures';
import { Color3, Matrix, Vector3 } from '@babylonjs/core/Maths/math';
import { Geometry } from '@babylonjs/core/Meshes/geometry';
import { LinesMesh } from '@babylonjs/core/Meshes/linesMesh';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { VertexData } from '@babylonjs/core/Meshes/mesh.vertexData';
import { Node } from '@babylonjs/core/node';
import { Scene } from '@babylonjs/core/scene';
import { Nullable } from '@babylonjs/core/types';
import { getBinClampedSize } from '@kb3d/common-util/texture-util';
import { AsyncZippable, AsyncZippableFile, strToU8 } from 'fflate';
import { map, take } from 'rxjs/operators';
import { SceneNode } from '../../../models/classes/scene-node';
import { Kb3dWorker } from '../../kb-worker';
import {
    buildHeader,
    buildUSDAFile,
    buildXform,
    ConversionResult,
    escapeGUID,
    GeometryTransferrable,
    PRECISION,
    USDOpts,
    USDZConverter,
} from './usdz-converter';
import { GeometryConversionPayload, TypedArray } from '@kb3d/models/interfaces/worker-types';

const canvasResize = document.createElement('canvas');

export class USDZBabylonConverter extends USDZConverter {
    parse(scene: Scene, sceneNode: SceneNode, useWorker = false) {
        const toCleanup: Array<Node | Geometry> = [];
        const files: Record<string, AsyncZippableFile> = {};
        const modelFileName = 'model.usda'; // model file should be first in USDZ archive so we init it here

        files[modelFileName] = new Uint8Array(0);
        let output = buildHeader(sceneNode);
        const materials: Record<string, PBRMaterial> = {};
        const textures: Record<string, Texture> = {};

        const conversionWorker = useWorker ? new Kb3dWorker() : null;
        const asyncGenerators: Array<Promise<void>> = [];

        scene.meshes.forEach(mesh => {
            if (
                mesh instanceof Mesh &&
                mesh.isEnabled() &&
                mesh.visibility > 0 &&
                mesh.geometry &&
                !(mesh instanceof LinesMesh) &&
                // for filtering out skyboxes
                !mesh.infiniteDistance
            ) {
                let subMeshes: Array<Nullable<Mesh>>;
                let geometries: Array<Nullable<Geometry>>;
                let meshMaterials: Array<Nullable<PBRMaterial>>;
                const baseGeometry = mesh.geometry;

                if (mesh.subMeshes && mesh.subMeshes.length > 1) {
                    const splitGeometryByMaterialId = new Map<
                        Material,
                        { indices: number[]; positions: number[]; normals: number[]; uvs: number[] }
                    >();

                    const baseVertexData = VertexData.ExtractFromGeometry(baseGeometry);
                    mesh.subMeshes.forEach((subMesh, index) => {
                        const subMeshMaterial: Nullable<Material> = subMesh.getMaterial();
                        if (subMeshMaterial) {
                            let vertexData = splitGeometryByMaterialId.get(subMeshMaterial);
                            if (!vertexData) {
                                vertexData = { indices: [], positions: [], normals: [], uvs: [] };
                                splitGeometryByMaterialId.set(subMeshMaterial, vertexData);
                            }
                            // Add polygons from this submesh to the mesh for this material
                            let newIdx = vertexData.indices.length;
                            const endIndex = subMesh.indexStart + subMesh.indexCount;
                            for (let i = subMesh.indexStart; i < endIndex; i++) {
                                if (baseVertexData.indices && baseVertexData.indices[i] !== undefined) {
                                    const oldIdx = baseVertexData.indices[i];

                                    vertexData.indices.push(newIdx);
                                    const i2 = oldIdx * 2;
                                    const i3 = oldIdx * 3;
                                    if (
                                        baseVertexData.positions &&
                                        typeof baseVertexData.positions[i3] !== 'undefined'
                                    ) {
                                        vertexData.positions.push(
                                            baseVertexData.positions[i3],
                                            baseVertexData.positions[i3 + 1],
                                            baseVertexData.positions[i3 + 2]
                                        );
                                    }
                                    if (baseVertexData.normals && typeof baseVertexData.normals[i3] !== 'undefined') {
                                        vertexData.normals.push(
                                            baseVertexData.normals[i3],
                                            baseVertexData.normals[i3 + 1],
                                            baseVertexData.normals[i3 + 2]
                                        );
                                    }
                                    if (baseVertexData.uvs && typeof baseVertexData.uvs[i2] !== 'undefined') {
                                        vertexData.uvs.push(baseVertexData.uvs[i2], baseVertexData.uvs[i2 + 1]);
                                    }

                                    newIdx++;
                                }
                            }
                        }

                        return null;
                    });

                    subMeshes = [];
                    geometries = [];
                    meshMaterials = [];
                    splitGeometryByMaterialId.forEach((vertexData, material) => {
                        if (material instanceof PBRMaterial) {
                            const newMesh = mesh.clone();
                            newMesh.id = mesh.id + '__' + material.id;
                            newMesh.material = material;
                            const newVertexData = new VertexData();
                            newVertexData.indices = vertexData.indices;
                            newVertexData.positions = vertexData.positions;
                            newVertexData.normals = vertexData.normals;
                            newVertexData.uvs = vertexData.uvs;
                            const newGeometry = Geometry.CreateGeometryForMesh(newMesh);
                            newGeometry.id = `sub-${newMesh.id}`;
                            newVertexData.applyToGeometry(newGeometry);
                            subMeshes.push(newMesh);
                            geometries.push(newGeometry);
                            meshMaterials.push(material);
                            toCleanup.push(newGeometry, newMesh);
                        } else {
                            console.warn(`USDZExporter: No PBR material on submesh`);
                        }
                    });
                } else {
                    subMeshes = [mesh];
                    geometries = [baseGeometry];
                    if (mesh.material instanceof PBRMaterial) {
                        meshMaterials = [mesh.material];
                    } else {
                        meshMaterials = [];
                        console.warn('USDZExporter: No PBR material on mesh');
                    }
                }

                for (let i = 0; i < geometries.length; i++) {
                    const subMesh = subMeshes[i];
                    const geometry = geometries[i];
                    const meshMaterial = meshMaterials[i];
                    if (!subMesh || !geometry || !meshMaterial) {
                        continue;
                    }

                    // const appendFileName = `__${i}`;
                    const geometryFileName = 'geometries/Geometry__' + escapeGUID(geometry.id) + '.usd';

                    if (!(geometryFileName in files)) {
                        if (conversionWorker) {
                            const geometryData = buildGeometryTransferrable(
                                { unitsPerMeter: sceneNode.unitsPerMeter, xrAllowScaling: sceneNode.xrAllowScaling },
                                geometry
                            );
                            if (geometryData) {
                                asyncGenerators.push(
                                    conversionWorker
                                        .startTask('usdConvertGeometry', geometryData.payload, geometryData.transfer)
                                        .pipe(
                                            take(1),
                                            map(res => {
                                                files[geometryFileName] = res.payload.file;
                                            })
                                        )
                                        .toPromise()
                                );
                            }
                        } else {
                            const meshObject = this.buildGeometryFromObject(geometry);
                            files[geometryFileName] = buildUSDAFile(sceneNode, meshObject);
                        }
                    }

                    if (!(meshMaterial.id in meshMaterial)) {
                        materials[meshMaterial.id] = meshMaterial;
                    }

                    output += buildXform(subMesh, geometry, meshMaterial);
                }
            }
        });

        output += buildMaterials(materials, textures);
        files[modelFileName] = strToU8(output);
        const textureGenerators: Array<() => Promise<void>> = [];
        for (const id in textures) {
            const texture = textures[id];
            const color = id.match(/[0-9A-F]{6}$/);
            const color3 = color && color.length === 1 ? Color3.FromHexString(`#${color[0]}`) : undefined;
            const isRGBA = texture.hasAlpha;
            textureGenerators.push(() =>
                processImageData(texture, isRGBA ? 'image/png' : 'image/jpeg', color3)
                    .then(blob => {
                        if (blob) {
                            return blob.arrayBuffer();
                        }
                    })
                    .then(buffer => {
                        if (buffer) {
                            files[`textures/Texture__${id}.${isRGBA ? 'png' : 'jpg'}`] = new Uint8Array(buffer);
                        }
                    })
            );
        } // 64 byte alignment
        // https://github.com/101arrowz/fflate/issues/39#issuecomment-777263109

        return Promise.all([processSequentially(asyncGenerators), processSequentially(textureGenerators)])
            .then(() => {
                let offset = 0;

                for (const filename in files) {
                    const file = files[filename] as Uint8Array;
                    const headerSize = 34 + filename.length;
                    offset += headerSize;
                    const offsetMod64 = offset & 63;

                    if (offsetMod64 !== 4) {
                        const padLength = 64 - offsetMod64;
                        const padding = new Uint8Array(padLength);
                        files[filename] = [
                            file,
                            {
                                extra: {
                                    12345: padding,
                                },
                            },
                        ];
                    }

                    offset = file.length;
                }

                if (conversionWorker) {
                    const transfer: ArrayBuffer[] = [];
                    for (const fileName in files) {
                        const file = files[fileName];
                        if (file instanceof Uint8Array) {
                            transfer.push(file.buffer);
                        } else {
                            transfer.push(file[0].buffer);
                        }
                    }

                    return conversionWorker
                        .startTask('usdPackageStart', files, transfer)
                        .pipe(
                            take(1),
                            map(res => {
                                canvasResize.width = 0;
                                canvasResize.height = 0;
                                conversionWorker.dispose();
                                return res.payload;
                            })
                        )
                        .toPromise();
                } else {
                    return this.zipUSDZFiles(files);
                }
            })
            .then(res => {
                toCleanup.forEach(item => item.dispose());
                return res;
            });
    }
}

async function processSequentially<T>(cbs: Array<Promise<T> | (() => Promise<T>)>) {
    const result: T[] = [];
    for (const cb of cbs) {
        if (cb instanceof Promise) {
            result.push(await cb);
        } else {
            result.push(await cb());
        }
    }

    return result;
}

export function processImageData(texture: Texture, mimeType: string, color?: Color3): Promise<Blob | undefined> {
    if (texture instanceof DynamicTexture) {
        const context = texture.getContext();

        const targetTexureSize = getBinClampedSize(context.canvas.width, context.canvas.height);
        canvasResize.width = targetTexureSize;
        canvasResize.height = targetTexureSize;

        const resizeContext = canvasResize.getContext('2d')!;
        const canvas = context.canvas;

        if (!(canvas instanceof HTMLCanvasElement)) {
            throw Error('texture does not have a canvas');
        }

        resizeContext.drawImage(canvas, 0, 0, targetTexureSize, targetTexureSize);

        if (color) {
            const imagedata = resizeContext.getImageData(0, 0, targetTexureSize, targetTexureSize);
            const data = imagedata.data;
            for (let i = 0; i < data.length; i += 4) {
                data[i + 0] = data[i + 0] * color.r;
                data[i + 1] = data[i + 1] * color.g;
                data[i + 2] = data[i + 2] * color.b;
            }
            resizeContext.putImageData(imagedata, 0, 0);
        }

        return canvasToBlob(canvasResize, mimeType);
    } else {
        const size = texture.getSize();

        const dataPromise = texture.readPixels() as Promise<TypedArray>;
        if (!dataPromise) {
            return Promise.resolve(undefined);
        }

        return dataPromise.then(data => {
            if (data instanceof Float32Array || data instanceof Float64Array) {
                for (let i = 0; i < data.length; i += 4) {
                    data[i + 0] = data[i + 0] * 255;
                    data[i + 1] = data[i + 1] * 255;
                    data[i + 2] = data[i + 2] * 255;
                    data[i + 3] = data[i + 3] * 255;
                }
            }

            let targetTexureSize = getBinClampedSize(size.width, size.height);
            if (targetTexureSize > 1024) {
                targetTexureSize = 1024;
            }

            canvasResize.width = targetTexureSize;
            canvasResize.height = targetTexureSize;

            const pxLength = targetTexureSize * targetTexureSize;
            const resizedData = new Uint8ClampedArray(pxLength * 4);
            if (targetTexureSize !== size.width || targetTexureSize !== size.height) {
                const widthRatio = size.width / targetTexureSize;
                const heightRatio = size.height / targetTexureSize;
                // in the interest of speed, just downscale with nearest neighbor algorithm instead of averaging pixels.
                for (let i = 0; i < pxLength; i++) {
                    const x = i % targetTexureSize;
                    // Y must be flipped for USDZ output
                    const y = Math.floor(targetTexureSize - i / targetTexureSize);
                    const targetIndex = (Math.floor(x * widthRatio) + Math.floor(y * heightRatio) * size.width) * 4;
                    const destIndex = i * 4;
                    resizedData[destIndex + 0] = data[targetIndex + 0];
                    resizedData[destIndex + 1] = data[targetIndex + 1];
                    resizedData[destIndex + 2] = data[targetIndex + 2];
                    resizedData[destIndex + 3] = data[targetIndex + 3];
                }
            } else {
                const lastLineOffset = (targetTexureSize - 1) * targetTexureSize;
                for (let y = 0; y < targetTexureSize / 2; y++) {
                    const yOffset = y * targetTexureSize;
                    const yOffsetTarget = lastLineOffset - yOffset;
                    for (let x = 0; x < targetTexureSize; x++) {
                        const offset = (yOffset + x) * 4;
                        const offsetTarget = (yOffsetTarget + x) * 4;
                        resizedData[offset] = data[offsetTarget];
                        resizedData[offset + 1] = data[offsetTarget + 1];
                        resizedData[offset + 2] = data[offsetTarget + 2];
                        resizedData[offset + 3] = data[offsetTarget + 3];
                        resizedData[offsetTarget] = data[offset];
                        resizedData[offsetTarget + 1] = data[offset + 1];
                        resizedData[offsetTarget + 2] = data[offset + 2];
                        resizedData[offsetTarget + 3] = data[offset + 3];
                    }
                }
            }

            if (color) {
                for (let i = 0; i < resizedData.length; i += 4) {
                    resizedData[i + 0] = resizedData[i + 0] * color.r;
                    resizedData[i + 1] = resizedData[i + 1] * color.g;
                    resizedData[i + 2] = resizedData[i + 2] * color.b;
                }
            }

            const imageData = new ImageData(resizedData, targetTexureSize, targetTexureSize);

            const resizeContext = canvasResize.getContext('2d')!;
            resizeContext.putImageData(imageData, 0, 0);

            return canvasToBlob(canvasResize, mimeType);
        });
    }
}

function canvasToBlob(canvasResize: HTMLCanvasElement, mimeType: string) {
    return new Promise<Blob>((resolve, reject) => {
        canvasResize.toBlob(
            result => {
                if (result) {
                    resolve(result);
                }
                reject('Could not generate texture');
            },
            mimeType,
            1
        );
    });
}

function buildMaterials(materials: Record<string, PBRMaterial>, textures: Record<string, Texture>) {
    const array = [];

    for (const uuid in materials) {
        const material = materials[uuid];
        array.push(buildMaterial(material, textures));
    }

    return `def "Materials"
{
${array.join('')}
}
`;
}

function buildMaterial(material: PBRMaterial, textures: Record<string, Texture>) {
    // https://graphics.pixar.com/usd/docs/UsdPreviewSurface-Proposal.html
    const pad = '            ';
    const inputs: string[] = [];
    const samplers: string[] = [];
    const materialId = escapeGUID(material.id);

    const newV = new Vector3();
    const tMatrix = new Matrix();
    const t2Matrix = new Matrix();
    const t3Matrix = new Matrix();

    function buildTexture(texture: BaseTexture, mapType: string, color?: Color3) {
        if (texture instanceof Texture) {
            const textureName = escapeGUID(texture.name);
            const parsedTextureId = textureName + (color ? '__' + color.toHexString().substring(1) : '');
            textures[parsedTextureId] = texture;
            const angleDeg = (texture.wAng * 180) / Math.PI;

            // USD doesn't seem to have a good way to apply a matrix to a texture. But we can use the rotation and scale straight,
            // and grab the translation off of the babylon texture matrix. The matrix includes the rotation pivot related translation
            // which is nasty to calculate without a matrix and USD doesn't support UV Pivot
            const textureMatrix = texture.getTextureMatrix().m;
            Matrix.RotationYawPitchRollToRef(texture.vAng, texture.uAng, texture.wAng, tMatrix);
            Matrix.ScalingToRef(texture.uScale, texture.vScale, 1, t2Matrix);
            tMatrix.multiplyToRef(t2Matrix, t3Matrix);
            t3Matrix.invert();

            Vector3.TransformCoordinatesFromFloatsToRef(textureMatrix[8], textureMatrix[9], 0, t3Matrix, newV);

            return `
        def Shader "Transform2d_${mapType}" (
            sdrMetadata = {
                string role = "math"
            }
        )
        {
            uniform token info:id = "UsdTransform2d"
            float2 inputs:in.connect = </Materials/Material__${materialId}/uvReader_st.outputs:result>
            float2 inputs:scale = (${texture.uScale}, ${texture.vScale})
            float2 inputs:translation = (${newV.x}, ${newV.y})
            float inputs:rotation = ${angleDeg.toPrecision(PRECISION)}
            float2 outputs:result
        }
        def Shader "Texture__${textureName}_${mapType}"
        {
            uniform token info:id = "UsdUVTexture"
            asset inputs:file = @./textures/Texture__${parsedTextureId}.${texture.hasAlpha ? 'png' : 'jpg'}@
            float2 inputs:st.connect = </Materials/Material__${materialId}/Transform2d_${mapType}.outputs:result>
            token inputs:wrapS = "repeat"
            token inputs:wrapT = "repeat"
            float outputs:r
            float outputs:g
            float outputs:b
            ${texture.hasAlpha ? 'float outputs:a' : ''}
            float3 outputs:rgb
        }`;
        }

        return '';
    }

    if (material.albedoTexture) {
        inputs.push(
            `${pad}color3f inputs:diffuseColor.connect = </Materials/Material__${materialId}/Texture__${escapeGUID(
                material.albedoTexture.name
            )}_diffuse.outputs:rgb>`
        );
        samplers.push(buildTexture(material.albedoTexture, 'diffuse', material.albedoColor));
    } else {
        inputs.push(`${pad}color3f inputs:diffuseColor = ${buildColor(material.albedoColor)}`);
    }

    if (material.emissiveTexture) {
        inputs.push(
            `${pad}color3f inputs:emissiveColor.connect = </Materials/Material__${materialId}/Texture__${escapeGUID(
                material.emissiveTexture.name
            )}_emissive.outputs:rgb>`
        );
        samplers.push(buildTexture(material.emissiveTexture, 'emissive'));
    } else if (material.emissiveColor.toLuminance() > 0) {
        inputs.push(`${pad}color3f inputs:emissiveColor = ${buildColor(material.emissiveColor)}`);
    }

    if (material.bumpTexture) {
        inputs.push(
            `${pad}normal3f inputs:normal.connect = </Materials/Material__${materialId}/Texture__${escapeGUID(
                material.bumpTexture.name
            )}_normal.outputs:rgb>`
        );
        samplers.push(buildTexture(material.bumpTexture, 'normal'));
    }

    if (material.ambientTexture !== null) {
        inputs.push(
            `${pad}float inputs:occlusion.connect = </Materials/Material__${materialId}/Texture__${escapeGUID(
                material.ambientTexture.name
            )}_occlusion.outputs:r>`
        );
        samplers.push(buildTexture(material.ambientTexture, 'occlusion'));
    }

    if (material.microSurfaceTexture !== null && material.roughness === 1) {
        inputs.push(
            `${pad}float inputs:roughness.connect = </Materials/Material__${materialId}/Texture__${escapeGUID(
                material.microSurfaceTexture.name
            )}_roughness.outputs:g>`
        );
        samplers.push(buildTexture(material.microSurfaceTexture, 'roughness'));
    } else {
        inputs.push(`${pad}float inputs:roughness = ${material.roughness}`);
    }

    if (material.metallicTexture !== null && material.metallic === 1) {
        inputs.push(
            `${pad}float inputs:metallic.connect = </Materials/Material__${materialId}/Texture__${escapeGUID(
                material.metallicTexture.name
            )}_metallic.outputs:b>`
        );
        samplers.push(buildTexture(material.metallicTexture, 'metallic'));
    } else {
        inputs.push(`${pad}float inputs:metallic = ${material.metallic}`);
    }

    if (material.opacityTexture !== null) {
        inputs.push(
            `${pad}float inputs:opacity.connect = </Materials/Material__${materialId}/Texture__${escapeGUID(
                material.opacityTexture.name
            )}_opacity.outputs:r>`
        );
        inputs.push(`${pad}float inputs:opacityThreshold = 0.0001`);
        samplers.push(buildTexture(material.opacityTexture, 'opacity'));
    } else if (material.albedoTexture && material.albedoTexture.hasAlpha) {
        inputs.push(
            `${pad}float inputs:opacity.connect = </Materials/Material__${materialId}/Texture__${escapeGUID(
                material.albedoTexture.name
            )}_diffuse.outputs:a>`
        );
        inputs.push(`${pad}float inputs:opacityThreshold = 0.0001`);
    } else {
        inputs.push(`${pad}float inputs:opacity = ${material.alpha}`);
    }

    if (material.clearCoat && material.clearCoat.isEnabled) {
        inputs.push(`${pad}float inputs:clearcoat = ${material.clearCoat.intensity}`);
        inputs.push(`${pad}float inputs:clearcoatRoughness = ${material.clearCoat.roughness}`);
        // TODO More properties?
    }
    inputs.push(`${pad}float inputs:ior = ${material.indexOfRefraction}`);
    // TODO More properties?

    return `
    def Material "Material__${materialId}"
    {
        def Shader "PreviewSurface"
        {
            uniform token info:id = "UsdPreviewSurface"
${inputs.join('\n')}
            int inputs:useSpecularWorkflow = 0
            token outputs:surface
        }
        token outputs:surface.connect = </Materials/Material__${materialId}/PreviewSurface.outputs:surface>
        token inputs:frame:stPrimvarName = "st"
        def Shader "uvReader_st"
        {
            uniform token info:id = "UsdPrimvarReader_float2"
            token inputs:varname.connect = </Materials/Material__${materialId}.inputs:frame:stPrimvarName>
            float2 inputs:fallback = (0.0, 0.0)
            float2 outputs:result
        }
${samplers.join('\n')}
    }
`;
}

function buildGeometryTransferrable(options: USDOpts, geometry: Geometry): GeometryTransferrable | undefined {
    const transfer: ArrayBuffer[] = [];
    const geometryBuffers = geometry.getVertexBuffers();
    const indices = geometry.getIndices();
    if (geometryBuffers && indices) {
        const vertexBuffers = Object.keys(geometryBuffers).reduce((res, type) => {
            const data = geometryBuffers[type].getData()!;

            if ('buffer' in data) {
                res[type] = data as Float32Array;
                transfer.push(res[type].slice().buffer);
            } else if (data instanceof ArrayBuffer || 'length' in data) {
                res[type] = new Float32Array(data);
                transfer.push(res[type].buffer);
            } else {
                console.warn('Unsupported geometry');
            }

            return res;
        }, {} as { [index: string]: Float32Array });

        const indexBuffer = Array.isArray(indices) ? new Uint32Array(indices) : indices;

        transfer.push(indexBuffer.slice().buffer);

        const result: GeometryTransferrable = {
            payload: {
                options,
                id: geometry.id,
                indices: indexBuffer,
                vertexBuffers,
            },
            transfer,
        };

        return result;
    }
}

function buildColor(color: Color3) {
    return `(${color.r}, ${color.g}, ${color.b})`;
}
